import { useState, useEffect, useCallback } from "react";
import { X, Users, Building, MapPin, FileText, User } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { insertarAsamblea } from "../../../../api/asambleaService";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { obtenerResidentes, Residente } from "../../../../api/residenteService";

interface CreateAsambleaModalProps {
  isOpen: boolean;
  onClose: (created: boolean) => void;
}

export default function CreateAsambleaModal({
  isOpen,
  onClose,
}: CreateAsambleaModalProps) {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamento, setSelectedApartamento] = useState("");
  const [selectedModerador, setSelectedModerador] = useState("");

  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [residentes, setResidentes] = useState<Residente[]>([]);

  const [isLoadingTorres, setIsLoadingTorres] = useState(false);
  const [isLoadingApartamentos, setIsLoadingApartamentos] = useState(false);
  const [isLoadingResidentes, setIsLoadingResidentes] = useState(false);



  const resetForm = () => {
    setTitulo("");
    setDescripcion("");
    setSelectedTorre("");
    setSelectedApartamento("");
    setSelectedModerador("");
    setApartamentos([]);
    setResidentes([]);
  };

  const loadTorres = useCallback(async () => {
    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      setIsLoadingTorres(true);
      const response = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });

      if (response.code === "1" && response.object_) {
        setTorres(response.object_);
      } else {
        setTorres([]);
      }
    } catch (error) {
      console.error("Error al cargar torres:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las torres",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoadingTorres(false);
    }
  }, [session?.unidad?.id]);

  useEffect(() => {
    if (isOpen) {
      loadTorres();
      resetForm();
    }
  }, [isOpen, loadTorres]);

  const loadApartamentos = async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentos([]);
      setResidentes([]);
      setSelectedApartamento("");
      setSelectedModerador("");
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      setIsLoadingApartamentos(true);
      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
      } else {
        setApartamentos([]);
      }
      setResidentes([]);
      setSelectedApartamento("");
      setSelectedModerador("");
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los apartamentos",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoadingApartamentos(false);
    }
  };

  const loadResidentes = async (apartamentoCodigo: string) => {
    if (!apartamentoCodigo || apartamentoCodigo.trim() === "") {
      setResidentes([]);
      setSelectedModerador("");
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      const torreCodigo = selectedTorre || "";
      if (!unidadCodigo || !torreCodigo) return;

      setIsLoadingResidentes(true);
      const response = await obtenerResidentes({
        "correo electronico usuario": "",
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
        "apartamento codigo": apartamentoCodigo,
        "tipo usuario codigo": "",
      });

      if (response.code === "1" && response.object_) {
        setResidentes(response.object_);
      } else {
        setResidentes([]);
      }
      setSelectedModerador("");
    } catch (error) {
      console.error("Error al cargar residentes:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los residentes",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsLoadingResidentes(false);
    }
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamento("");
    setSelectedModerador("");
    setApartamentos([]);
    setResidentes([]);
    if (value) {
      loadApartamentos(value);
    }
  };

  const handleApartamentoChange = (value: string) => {
    setSelectedApartamento(value);
    setSelectedModerador("");
    setResidentes([]);
    if (value) {
      loadResidentes(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!titulo.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el título de la asamblea",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!descripcion.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la descripción de la asamblea",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedModerador) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione un moderador",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const unidadCodigo = session?.unidad?.id || "";
      const usuarioAdministrador = session?.email || "";

      if (!unidadCodigo || !usuarioAdministrador) {
        throw new Error("No se pudo obtener la información de la sesión");
      }

      const payload = {
        unidad_residencial_codigo: unidadCodigo,
        usuario_administrador: usuarioAdministrador,
        usuario_moderador: selectedModerador,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        estatus_codigo: "N",
      };

      const response = await insertarAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Asamblea creada!",
          text: response.message || "La asamblea se ha creado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al crear la asamblea");
      }
    } catch (error) {
      console.error("Error al crear asamblea:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al crear la asamblea";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al crear la asamblea. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <Users size={28} />
              Crear Nueva Asamblea
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Complete todos los campos para crear una nueva asamblea
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText size={16} className="inline mr-1" />
              Título <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej: Asamblea No.234 se realizará el 19/01/2026"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText size={16} className="inline mr-1" />
              Descripción <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción detallada de la asamblea..."
              disabled={isSubmitting}
              className="min-h-[120px]"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building size={16} className="inline mr-1" />
                Torre
              </label>
              {isLoadingTorres ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                  <span className="text-sm">Cargando torres...</span>
                </div>
              ) : (
                <select
                  value={selectedTorre}
                  onChange={(e) => handleTorreChange(e.target.value)}
                  disabled={isSubmitting || isLoadingTorres}
                  className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                >
                  <option value="">Seleccione una torre</option>
                  {torres.map((torre) => (
                    <option key={torre.tor_codigo} value={torre.tor_codigo}>
                      {torre.tor_nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Apartamento
              </label>
              {isLoadingApartamentos ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                  <span className="text-sm">Cargando apartamentos...</span>
                </div>
              ) : (
                <select
                  value={selectedApartamento}
                  onChange={(e) => handleApartamentoChange(e.target.value)}
                  disabled={isSubmitting || isLoadingApartamentos || !selectedTorre}
                  className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                >
                  <option value="">
                    {selectedTorre ? "Seleccione un apartamento" : "Primero seleccione una torre"}
                  </option>
                  {apartamentos.map((apt) => (
                    <option key={apt.apt_codigo} value={apt.apt_codigo}>
                      {apt.apt_nombre}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User size={16} className="inline mr-1" />
              Moderador <span className="text-red-500">*</span>
            </label>
            {isLoadingResidentes ? (
              <div className="flex items-center gap-2 text-gray-500">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                <span className="text-sm">Cargando residentes...</span>
              </div>
            ) : (
              <select
                value={selectedModerador}
                onChange={(e) => setSelectedModerador(e.target.value)}
                disabled={isSubmitting || isLoadingResidentes || !selectedApartamento}
                className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                required
              >
                <option value="">
                  {selectedApartamento
                    ? "Seleccione un moderador"
                    : "Primero seleccione un apartamento"}
                </option>
                {residentes
                  .sort((a, b) => {
                    const tipoA = a.res_tipo_usuario || "";
                    const tipoB = b.res_tipo_usuario || "";
                    if (tipoA === "PR") return -1;
                    if (tipoB === "PR") return 1;
                    return 0;
                  })
                  .map((residente) => (
                    <option
                      key={residente.res_email || residente.res_codigo}
                      value={residente.res_email || ""}
                    >
                      {residente.res_nombre_completo ||
                        residente.res_nombre ||
                        residente.res_email ||
                        "Sin nombre"}
                      {residente.res_tipo_usuario_nombre &&
                        ` - ${residente.res_tipo_usuario_nombre}`}
                      {residente.res_email && ` (${residente.res_email})`}
                    </option>
                  ))}
              </select>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button
              type="button"
              onClick={() => onClose(false)}
              variant="outline"
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-military-500 hover:bg-military-600 text-white"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Guardando...
                </>
              ) : (
                "Guardar Asamblea"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
